import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, Trash2, X } from 'lucide-react';
import AdminShell from '../components/AdminShell';
import BottomSheetAction from '../components/BottomSheetAction';
import { useApp } from '../lib/AppContext';
import { useAuth } from '../lib/AuthContext';
import {
  getSongById,
  createSong,
  updateSong,
  getSongPrimaryTags,
  updateSongPrimaryTags,
  generateUniqueSlug,
  deleteSong,
  updateSongSecondaryTags,
  getSongSecondaryTags,
  SongDetail
} from '../lib/supabase/songs';
import SongEditorSongTab from '../components/SongEditorSongTab';
import SongEditorStoryNew from '../components/SongEditorStoryNew';
import ActionModalsEditorNew, { type ActionModalsEditorHandle } from '../components/ActionModalsEditorNew';

type EditorTab = 'song' | 'story' | 'actions';

export default function SongEditorUnifiedNew() {
  const { currentCardId, setCurrentCardId, navigateTo, showToast, refreshSongs } = useApp();
  const { user } = useAuth();

  const [songData, setSongData] = useState<Partial<SongDetail> | null>(null);
  const [primaryTagIds, setPrimaryTagIds] = useState<string[]>([]);
  const [secondaryTagNames, setSecondaryTagNames] = useState<string[]>([]);
  const [originalSongData, setOriginalSongData] = useState<Partial<SongDetail> | null>(null);
  const [originalPrimaryTagIds, setOriginalPrimaryTagIds] = useState<string[]>([]);
  const [originalSecondaryTagNames, setOriginalSecondaryTagNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasUnsavedActionChanges, setHasUnsavedActionChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<EditorTab>('song');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Ref for action modals editor
  const actionModalsEditorRef = useRef<ActionModalsEditorHandle>(null);

  useEffect(() => {
    if (currentCardId) {
      loadSong();
    } else {
      initializeNewSong();
    }
  }, [currentCardId]);

  async function loadSong(songId?: string) {
    const idToLoad = songId || currentCardId;
    if (!idToLoad) return;
    setLoading(true);
    try {
      const { data: song, error } = await getSongById(idToLoad);

      if (error || !song) {
        showToast('Song not found');
        navigateTo('admin-songs');
        return;
      }

      setSongData(song);
      setOriginalSongData(song);

      // Load primary tags
      const { data: primaryTags } = await getSongPrimaryTags(idToLoad);
      if (primaryTags) {
        setPrimaryTagIds(primaryTags);
        setOriginalPrimaryTagIds(primaryTags);
      }

      // Load secondary tags based on status
      if (song.status === 'draft' && song.draft_secondary_tags) {
        // For drafts, load from draft_secondary_tags field
        setSecondaryTagNames(song.draft_secondary_tags);
        setOriginalSecondaryTagNames(song.draft_secondary_tags);
      } else if (song.status === 'published') {
        // For published songs, load from song_tag_map
        const { data: secondaryTags } = await getSongSecondaryTags(idToLoad);
        const tagNames = secondaryTags ? secondaryTags.map(tag => tag.name) : [];
        setSecondaryTagNames(tagNames);
        setOriginalSecondaryTagNames(tagNames);
      }

      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to load song:', err);
      showToast('Failed to load song');
      navigateTo('admin-songs');
    } finally {
      setLoading(false);
    }
  }

  function initializeNewSong() {
    const newSongData = {
      title: '',
      artist: '',
      slug: '',
      status: 'draft',
      description: null,
      lyric_spotlight: null,
      story_body: null,
      image_url: null,
      spotify_url: null,
      apple_music_url: null,
      youtube_url: null,
      tags: [],
      themes: [],
      is_active: false,
    };
    setSongData(newSongData);
    setOriginalSongData(newSongData);
    setPrimaryTagIds([]);
    setOriginalPrimaryTagIds([]);
    setSecondaryTagNames([]);
    setOriginalSecondaryTagNames([]);
    setHasUnsavedChanges(false);
    setLoading(false);
  }

  // Detect if there are actual changes from the original state
  function detectChanges(): boolean {
    if (!originalSongData || !songData) return false;

    // Helper to normalize empty values (treat null and empty string as equivalent)
    const normalizeValue = (val: any) => {
      if (val === null || val === undefined || val === '') return null;
      return val;
    };

    // Compare song data fields
    const fieldsToCompare: (keyof SongDetail)[] = [
      'title', 'artist', 'description', 'lyric_spotlight', 'story_body',
      'image_url', 'spotify_url', 'apple_music_url', 'youtube_url',
      'region', 'country', 'deep_insight', 'quick_fact', 'context_in_time',
      'meta_title', 'meta_description'
    ];

    for (const field of fieldsToCompare) {
      if (normalizeValue(songData[field]) !== normalizeValue(originalSongData[field])) {
        return true;
      }
    }

    // Compare primary tags
    if (primaryTagIds.length !== originalPrimaryTagIds.length ||
        !primaryTagIds.every((id, index) => id === originalPrimaryTagIds[index])) {
      return true;
    }

    // Compare secondary tags
    if (secondaryTagNames.length !== originalSecondaryTagNames.length ||
        !secondaryTagNames.every((name, index) => name === originalSecondaryTagNames[index])) {
      return true;
    }

    return false;
  }

  // Update hasUnsavedChanges whenever relevant data changes
  useEffect(() => {
    if (loading) return; // Don't detect changes while loading
    const hasChanges = detectChanges();
    setHasUnsavedChanges(hasChanges);
  }, [songData, primaryTagIds, secondaryTagNames, originalSongData, originalPrimaryTagIds, originalSecondaryTagNames, loading]);

  function updateSongData(updates: Partial<SongDetail>) {
    setSongData({ ...songData, ...updates } as Partial<SongDetail>);
  }

  // Compute total unsaved changes (song + actions)
  const hasAnyUnsavedChanges = hasUnsavedChanges || hasUnsavedActionChanges;

  function handleBack() {
    if (hasAnyUnsavedChanges) {
      setPendingNavigation(() => () => navigateTo('admin-songs'));
      setShowUnsavedDialog(true);
      return;
    }
    navigateTo('admin-songs');
  }

  function handleDiscardChanges() {
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }

  function handleContinueEditing() {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  }

  async function handleSaveAndNavigate() {
    setShowUnsavedDialog(false);
    const success = await handleSave(false, true); // Save as draft, skip auto-navigation
    if (success && pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }

  function validateSong(): { valid: boolean; message?: string } {
    if (!songData?.title?.trim()) {
      return { valid: false, message: 'Song title is required' };
    }
    if (!songData?.artist?.trim()) {
      return { valid: false, message: 'Artist name is required' };
    }

    // Validate URLs if provided
    const urlFields = [
      { key: 'spotify_url', label: 'Spotify URL' },
      { key: 'apple_music_url', label: 'Apple Music URL' },
      { key: 'youtube_url', label: 'YouTube URL' },
    ];

    for (const field of urlFields) {
      const value = songData[field.key as keyof typeof songData] as string | null;
      if (value && value.trim()) {
        try {
          new URL(value);
        } catch {
          return { valid: false, message: `${field.label} must be a valid URL` };
        }
      }
    }

    return { valid: true };
  }

  async function handleSave(publish: boolean, skipNavigation = false): Promise<boolean> {
    if (!user) {
      showToast('You must be logged in');
      return false;
    }

    const validation = validateSong();
    if (!validation.valid) {
      showToast(validation.message || 'Validation failed');
      return false;
    }

    setSaving(true);
    try {
      // Save action modals first if there are unsaved changes and editor is mounted
      if (hasUnsavedActionChanges && currentCardId && actionModalsEditorRef.current) {
        const actionsSaved = await actionModalsEditorRef.current.save();
        if (!actionsSaved) {
          throw new Error('Failed to save action modals');
        }
      }
      const isNewSong = !currentCardId;

      // Auto-generate slug
      const slug = await generateUniqueSlug(
        songData!.title!,
        songData!.artist!,
        currentCardId || undefined
      );

      const payload = {
        ...songData,
        slug,
        status: publish ? 'published' as const : 'draft' as const,
        // Save secondary tags in draft_secondary_tags for drafts
        draft_secondary_tags: publish ? [] : secondaryTagNames,
      };

      let savedSongId: string;

      if (isNewSong) {
        const { data: newSong, error } = await createSong(payload as any, user.id);
        if (error || !newSong) {
          throw new Error(error?.message || 'Failed to create song');
        }
        savedSongId = newSong.id;

        // Update primary tags before navigating
        const { error: newSongTagsError } = await updateSongPrimaryTags(savedSongId, primaryTagIds);
        if (newSongTagsError) throw new Error('Failed to save primary tags');

        // Sync secondary tags to song_tag_map when publishing
        if (publish) {
          const { error: secTagsError } = await updateSongSecondaryTags(savedSongId, secondaryTagNames);
          if (secTagsError) throw new Error('Failed to save secondary tags');
        }

        // Refresh songs data so preview shows updated tags
        await refreshSongs();

        showToast(publish ? 'Song created successfully' : 'Draft saved successfully');

        // For new songs, navigate to the editor with the new song ID
        if (!skipNavigation) {
          navigateTo('song-editor', savedSongId);
        } else {
          // Update original state to match current state (resets change tracking)
          const updatedSongData = { ...songData, ...payload };
          setSongData(updatedSongData);
          setOriginalSongData(updatedSongData);
          setOriginalPrimaryTagIds([...primaryTagIds]);
          setOriginalSecondaryTagNames([...secondaryTagNames]);
        }
      } else {
        const { data: updatedSong, error } = await updateSong(currentCardId, payload, user.id);
        if (error || !updatedSong) {
          throw new Error(error?.message || 'Failed to update song');
        }
        savedSongId = currentCardId;

        // Update primary tags
        const { error: tagsError } = await updateSongPrimaryTags(savedSongId, primaryTagIds);
        if (tagsError) throw new Error('Failed to save primary tags');

        // Sync secondary tags to song_tag_map when publishing
        if (publish) {
          const { error: secTagsError } = await updateSongSecondaryTags(savedSongId, secondaryTagNames);
          if (secTagsError) throw new Error('Failed to save secondary tags');
        }

        // Refresh songs data so preview shows updated tags
        await refreshSongs();

        showToast(publish ? 'Song updated successfully' : 'Draft saved successfully');

        // Update original state to match current state (resets change tracking)
        const updatedSongData = { ...songData, ...payload };
        setSongData(updatedSongData);
        setOriginalSongData(updatedSongData);
        setOriginalPrimaryTagIds([...primaryTagIds]);
        setOriginalSecondaryTagNames([...secondaryTagNames]);

        if (!skipNavigation && publish) {
          navigateTo('admin-songs');
        }
      }

      return true;
    } catch (err: any) {
      console.error('Error saving song:', err);
      showToast(err.message || 'An error occurred while saving');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!currentCardId || !user) return;

    setDeleting(true);
    try {
      const { error } = await deleteSong(currentCardId, user.id);
      if (error) {
        throw new Error(error.message || 'Failed to delete song');
      }

      showToast('Song deleted successfully');
      navigateTo('admin-songs');
    } catch (err: any) {
      console.error('Error deleting song:', err);
      showToast(err.message || 'An error occurred while deleting');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  if (loading) {
    return (
      <AdminShell activeSection="songs">
        <div className="p-8">
          <div className="text-center py-20 text-[#808080]">Loading song...</div>
        </div>
      </AdminShell>
    );
  }

  if (!songData) return null;

  const isPublished = songData.status === 'published';
  const isNewSong = !currentCardId;

  const tabs: { key: EditorTab; label: string }[] = [
    { key: 'song', label: 'Song' },
    { key: 'story', label: 'Story' },
    { key: 'actions', label: 'Actions' },
  ];

  return (
    <AdminShell activeSection="songs">
      <div className="min-h-screen bg-[#111111] pb-24 md:pb-0">
        {/* Desktop Header */}
        <div className="hidden md:block sticky top-0 z-20 bg-[#111111]/95 backdrop-blur-sm border-b border-[#282828]">
          <div className="max-w-6xl px-8 py-4">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-[14px] font-medium">Back to Songs</span>
              </button>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleBack}
                  className="px-4 py-2 rounded-lg text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#1A1A1A] text-[13px] font-medium transition-colors"
                >
                  Cancel
                </button>
                {!isNewSong && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B6B]/10 text-[#FF6B6B] border border-[#FF6B6B]/30 text-[13px] font-medium hover:bg-[#FF6B6B]/20 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                )}
                <button
                  onClick={() => handleSave(true, !isNewSong)}
                  disabled={saving || !hasAnyUnsavedChanges}
                  className="flex items-center gap-2 px-5 py-2 bg-[#40E0D0] hover:bg-[#47EAD8] rounded-lg text-[#0A0A0A] text-[13px] font-semibold transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {saving ? (isPublished ? 'Updating...' : 'Publishing...') : (isPublished ? 'Update' : 'Publish')}
                </button>
              </div>
            </div>

            {/* Desktop Tabs */}
            <div className="flex items-center gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
                    activeTab === tab.key
                      ? 'bg-[#40E0D0] text-[#0A0A0A]'
                      : 'text-[#A0A0A0] hover:text-[#F5F5F5] hover:bg-[#1A1A1A]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Header */}
        <div className="md:hidden sticky top-0 z-20 bg-[#111111]/95 backdrop-blur-sm border-b border-[#282828]">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={handleBack}
              className="p-2 -ml-2 rounded-lg transition-colors hover:bg-[#1A1A1A] active:bg-[#1A1A1A]"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5 text-[#F5F5F5]" />
            </button>

            <h1 className="text-[16px] font-semibold text-[#F5F5F5]">
              {isNewSong ? 'New Song' : 'Edit Song'}
            </h1>

            <div className="w-9 h-9" />
          </div>

          {/* Mobile Tab Selector */}
          <div className="px-4 pb-3">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as EditorTab)}
              className="w-full px-4 py-2.5 bg-[#1A1A1A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] focus:outline-none focus:border-[#40E0D0]/50"
            >
              {tabs.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-6xl px-4 py-6 md:px-8 md:py-8 md:pb-8 pb-32">
          {activeTab === 'song' && (
            <SongEditorSongTab
              songData={songData}
              songId={currentCardId || undefined}
              primaryTagIds={primaryTagIds}
              secondaryTagNames={secondaryTagNames}
              onUpdate={updateSongData}
              onPrimaryTagsChange={setPrimaryTagIds}
              onSecondaryTagsChange={setSecondaryTagNames}
            />
          )}

          {activeTab === 'story' && (
            <SongEditorStoryNew
              songData={songData}
              onUpdate={updateSongData}
            />
          )}

          {activeTab === 'actions' && (
            <ActionModalsEditorNew
              ref={actionModalsEditorRef}
              songId={currentCardId}
              onContentChange={setHasUnsavedActionChanges}
            />
          )}
        </div>

        {/* Mobile Bottom Action Bar */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#111111]/95 backdrop-blur-sm border-t border-[#282828] pb-safe">
          <div className="px-4 py-3">
            <button
              onClick={() => handleSave(true, !isNewSong)}
              disabled={saving || !hasAnyUnsavedChanges}
              className="w-full px-4 py-3 bg-[#40E0D0] hover:bg-[#47EAD8] active:bg-[#3DD4C4] rounded-lg text-[#0A0A0A] text-[15px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? (isPublished ? 'Updating...' : 'Publishing...') : (isPublished ? 'Update' : 'Publish')}
            </button>
            {!isNewSong && (
              <button
                onClick={() => setShowMobileMenu(true)}
                className="w-full mt-2 py-2 text-[13px] text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
              >
                More options
              </button>
            )}
          </div>
        </div>

        {/* Mobile More Options Menu */}
        {showMobileMenu && (
          <div className="md:hidden fixed inset-0 z-40 flex items-end" onClick={() => setShowMobileMenu(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div
              className="relative w-full bg-zinc-900/95 border-t border-white/10 rounded-t-2xl shadow-2xl pb-safe"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
                <h3 className="text-[16px] font-semibold text-zinc-100">More Options</h3>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 -mr-2 rounded-lg hover:bg-white/[0.06] transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              <div className="p-4 space-y-2">
                <BottomSheetAction
                  label="Delete Song"
                  onClick={() => {
                    setShowMobileMenu(false);
                    setShowDeleteConfirm(true);
                  }}
                  variant="danger"
                  disabled={saving || deleting}
                />
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
            <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-6 w-full max-w-md">
              <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-2">Delete song?</h3>
              <p className="text-[14px] text-[#A0A0A0] mb-6">
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 bg-[#282828] hover:bg-[#333333] active:bg-[#333333] rounded-lg text-[#F5F5F5] text-[14px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 bg-[#FF6B6B] hover:bg-[#FF5252] active:bg-[#E84545] rounded-lg text-white text-[14px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unsaved Changes Dialog */}
        {showUnsavedDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
            <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-6 w-full max-w-md">
              <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-2">Discard changes?</h3>
              <p className="text-[14px] text-[#A0A0A0] mb-6">
                You have unsaved changes that will be lost.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleContinueEditing}
                  className="w-full px-4 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] rounded-lg text-[#0A0A0A] text-[14px] font-semibold transition-colors"
                >
                  Keep editing
                </button>
                <button
                  onClick={handleDiscardChanges}
                  className="w-full px-4 py-2.5 bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 hover:bg-[#FF6B6B]/20 rounded-lg text-[#FF6B6B] text-[14px] font-medium transition-colors"
                >
                  Discard changes
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </AdminShell>
  );
}
